import React from "react";

import ResponsiveTable from "../../../containers/responsive-table";
import Row from "../../../containers/responsive-table/row";
import TBody from "../../../containers/responsive-table/tbody";
import HeadCell from "../../../containers/responsive-table/head-cell";
import BodyCell from "../../../containers/responsive-table/body-cell";
import { decimalsRound } from "../../../services/utils";
import Thead from "../../../containers/responsive-table/thead";
import styled from 'styled-components';

const getBackgroundVariant = (value, itemRound, currentRound) => {
  if (itemRound === currentRound && value > 0) {
    return "highlighted";
  } else if (value <= 0) {
    return "disabled";
  } else {
    return "default";
  }
};

const TableWrapper = styled.div`
  padding: 20px;

  ${p => p.padding && `
    padding: ${p.padding};
  `}
`;

const TableHeaderText = styled.div`
  font-family: 'Mukta Vaani';
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);
`;

const FinancialTargets = props => {
  const { round, financialTargets, hideTitle, title = 'Financial Metrics & Targets', padding, onHelpIconClick } = props;

  const shouldShowMetric = (metricKey) => {
    return financialTargets.some(target => {
      const value = parseFloat(target[metricKey]);
      return value > 0;
    });
  };

  // Determine which columns to show
  const showMarketShare = shouldShowMetric('market_share');
  const showRevenues = shouldShowMetric('net_revenues');
  const showGrossMargins = shouldShowMetric('gross_margin');
  const showSharePrice = shouldShowMetric('stock_price');
  const showROE = shouldShowMetric('roe');
  const showROA = shouldShowMetric('roa');
  const showEPS = shouldShowMetric('earnings_per_share');

  return (
    <TableWrapper padding={padding}>
      {!hideTitle && <TableHeaderText>{title}</TableHeaderText>}
      <ResponsiveTable>
        <Thead>
          <Row>
            <HeadCell iconType="sort" cellSize="small">
              Year
            </HeadCell>
            {showMarketShare && (
              <HeadCell iconType={onHelpIconClick ? "help" : null} onHelpIconClick={onHelpIconClick ? () => onHelpIconClick("Market Share") : null}>
                Market Share
              </HeadCell>
            )}
            {showRevenues && (
              <HeadCell iconType={onHelpIconClick ? "help" : null} onHelpIconClick={onHelpIconClick ? () => onHelpIconClick("Revenues") : null}>
                Revenues
              </HeadCell>
            )}
            {showGrossMargins && (
              <HeadCell iconType={onHelpIconClick ? "help" : null} onHelpIconClick={onHelpIconClick ? () => onHelpIconClick("Gross Margins") : null}>
                Gross Margins
              </HeadCell>
            )}
            {showSharePrice && (
              <HeadCell iconType={onHelpIconClick ? "help" : null} onHelpIconClick={onHelpIconClick ? () => onHelpIconClick("Share Price") : null}>
                Share Price
              </HeadCell>
            )}
            {showROE && (
              <HeadCell iconType={onHelpIconClick ? "help" : null} onHelpIconClick={onHelpIconClick ? () => onHelpIconClick("ROE") : null}>
                ROE
              </HeadCell>
            )}
            {showROA && (
              <HeadCell iconType={onHelpIconClick ? "help" : null} onHelpIconClick={onHelpIconClick ? () => onHelpIconClick("ROA") : null}>
                ROA
              </HeadCell>
            )}
            {showEPS && (
              <HeadCell iconType={onHelpIconClick ? "help" : null} onHelpIconClick={onHelpIconClick ? () => onHelpIconClick("EPS") : null}>
                EPS
              </HeadCell>
            )}
          </Row>
        </Thead>
        <TBody>
          {financialTargets.map((item, index) => {
            // Check which metrics are active for this specific item
            const itemHasMarketShare = parseFloat(item.market_share) > 0;
            const itemHasRevenues = parseFloat(item.net_revenues) > 0;
            const itemHasGrossMargins = parseFloat(item.gross_margin) > 0;
            const itemHasSharePrice = parseFloat(item.stock_price) > 0;
            const itemHasROE = parseFloat(item.roe) > 0;
            const itemHasROA = parseFloat(item.roa) > 0;
            const itemHasEPS = parseFloat(item.earnings_per_share) > 0;

            return (
              <Row key={`financial-targets-row-${index}`} isBody>
                <BodyCell
                  alignText="center"
                  cellSize="small"
                  backgroundVariant="default"
                >
                  {item.round_number}
                </BodyCell>
                {showMarketShare && (
                  <BodyCell
                    backgroundVariant={getBackgroundVariant(
                      item.market_share,
                      item.round_number,
                      round
                    )}
                  >
                    {itemHasMarketShare
                      ? `${decimalsRound(item.market_share * 100, 2)}%`
                      : "--"}
                  </BodyCell>
                )}
                {showRevenues && (
                  <BodyCell
                    backgroundVariant={getBackgroundVariant(
                      item.net_revenues,
                      item.round_number,
                      round
                    )}
                  >
                    {itemHasRevenues
                      ? `$${decimalsRound(item.net_revenues / 1000000, 2)}M`
                      : "--"}
                  </BodyCell>
                )}
                {showGrossMargins && (
                  <BodyCell
                    backgroundVariant={getBackgroundVariant(
                      item.gross_margin,
                      item.round_number,
                      round
                    )}
                  >
                    {itemHasGrossMargins
                      ? `${decimalsRound(item.gross_margin * 100, 2)}%`
                      : "--"}
                  </BodyCell>
                )}
                {showSharePrice && (
                  <BodyCell
                    backgroundVariant={getBackgroundVariant(
                      item.stock_price,
                      item.round_number,
                      round
                    )}
                  >
                    {itemHasSharePrice
                      ? `$${decimalsRound(item.stock_price, 2)}`
                      : "--"}
                  </BodyCell>
                )}
                {showROE && (
                  <BodyCell
                    backgroundVariant={getBackgroundVariant(
                      item.roe,
                      item.round_number,
                      round
                    )}
                  >
                    {itemHasROE ? `${decimalsRound(item.roe * 100, 2)}%` : "--"}
                  </BodyCell>
                )}
                {showROA && (
                  <BodyCell
                    backgroundVariant={getBackgroundVariant(
                      item.roa,
                      item.round_number,
                      round
                    )}
                  >
                    {itemHasROA ? `${decimalsRound(item.roa * 100, 2)}%` : "--"}
                  </BodyCell>
                )}
                {showEPS && (
                  <BodyCell
                    backgroundVariant={getBackgroundVariant(
                      item.earnings_per_share,
                      item.round_number,
                      round
                    )}
                  >
                    {itemHasEPS
                      ? `$${decimalsRound(item.earnings_per_share, 2)}`
                      : "--"}
                  </BodyCell>
                )}
              </Row>
            );
          })}
        </TBody>
      </ResponsiveTable>
    </TableWrapper>
  );
};

export default FinancialTargets;



// WEBPACK FOOTER //
// ./src/components/performance-feedback/components/FinancialTargets.js